import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useBrands } from "@/hooks/use-brands";
import { apiFetch } from "@/lib/api-client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useWizardStore } from "@/stores/wizard-store";
import { useAuth } from "@/hooks/use-auth";
import { usePortfolio } from "@/hooks/use-portfolio";
import { PortfolioCard } from "@/components/dashboard/portfolio-card";
import { NeedsAttentionStrip } from "@/components/dashboard/needs-attention-strip";

/**
 * Landing page after OAuth login.
 * - If user has exactly one brand → redirect to it.
 * - If user has 2+ brands → portfolio dashboard (attention strip + card grid).
 * - If no brands → show "Create your first brand" form.
 */
export function DashboardPage() {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const { data: brands, isLoading } = useBrands();
  const { openWizard } = useWizardStore();
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isLoading && brands && brands.length === 1) {
      navigate(`/${brands[0].slug}`, { replace: true });
    }
  }, [brands, isLoading, navigate]);

  if (isLoading || (brands && brands.length === 1)) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="space-y-3 w-64">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      </div>
    );
  }

  if (brands && brands.length >= 2) {
    return <PortfolioDashboard />;
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const brand = await apiFetch<{ slug: string }>("/brands", {
        method: "POST",
        body: JSON.stringify({ name: name.trim() }),
      });
      openWizard(0); // open wizard when the brand page mounts
      navigate(`/${brand.slug}`);
    } catch (err: any) {
      setError(err?.message ?? t("dashboard.somethingWentWrong"));
      setSubmitting(false);
    }
  }

  if (!user?.isSuperAdmin) {
    return (
      <div className="flex h-full items-center justify-center px-6 py-8">
        <div className="w-full max-w-sm space-y-3 text-center">
          <h1 className="text-2xl font-bold tracking-tight">{t("dashboard.welcomeTitle")}</h1>
          <p className="text-sm text-muted-foreground">
            {t("dashboard.noBrandsMessage")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full items-center justify-center px-6 py-8">
      <div className="w-full max-w-sm space-y-6">
        <div className="space-y-1 text-center">
          <h1 className="text-2xl font-bold tracking-tight">{t("dashboard.welcomeTitle")}</h1>
          <p className="text-sm text-muted-foreground">
            {t("dashboard.createFirstBrand")}
          </p>
        </div>

        <form onSubmit={handleCreate} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="brand-name">{t("dashboard.brandNameLabel")}</Label>
            <Input
              id="brand-name"
              placeholder={t("dashboard.brandNamePlaceholder")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={submitting}
              autoFocus
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button type="submit" className="w-full" disabled={submitting || !name.trim()}>
            {submitting ? t("dashboard.creating") : t("dashboard.createBrand")}
          </Button>
        </form>
      </div>
    </div>
  );
}

/** Multi-brand portfolio: needs-attention strip + per-brand summary cards. */
function PortfolioDashboard() {
  const { t } = useTranslation("auth");
  const { data, isLoading, isError, refetch } = usePortfolio();
  const portfolioBrands = data?.brands ?? [];

  return (
    <div className="px-6 py-6 space-y-4 max-w-6xl mx-auto">
      <div>
        <h1 className="text-xl font-bold tracking-tight">{t("dashboard.portfolio.title")}</h1>
        <p className="text-sm text-muted-foreground">
          {t("dashboard.portfolio.subtitle")}
        </p>
      </div>

      {isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-64 rounded-lg" />
          ))}
        </div>
      ) : isError ? (
        <div className="flex items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2.5">
          <p className="text-sm text-destructive">{t("dashboard.portfolio.loadError")}</p>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            {t("dashboard.portfolio.retry")}
          </Button>
        </div>
      ) : (
        <>
          <NeedsAttentionStrip brands={portfolioBrands} />
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {portfolioBrands.map((b) => (
              <PortfolioCard key={b.id} brand={b} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
